//src/app/features/conversations/ConversationsListProvider.tsx
import useDebouncedFilters from 'app/hooks/useDebouncedFilters';
import { IGetQueryParams } from 'app/services';
import { useLazyGetPaginatedConversationsQuery } from 'app/services/conversations';
import { IConversation } from 'app/services/messages';
import { uniqBy } from 'lodash';
import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUnreadConversations } from 'utils/ConversationsNotificationsProvider';

interface QueryParams
  extends IGetQueryParams<{
    stared?: boolean;
    archived?: boolean;
    title_search: string;
    client_id?: string;
    read?: boolean;
  }> {}

export const ConversationsListContext = createContext<{
  data?: IConversation[];
  params: QueryParams;
  toggleIsArchived: () => void;
  toggleIsStared: () => void;
  toggleIsRead: () => void;
  loadMore: () => void;
  setSearch: (title_search: string) => void;
  refreshTop: (limit?: number) => void;
  patchConversation: (updated: IConversation) => void;
  addConversation: (added: IConversation) => void;
  hasMore: boolean;
  isLoading: boolean;
  markAsRead: (conversation: IConversation) => void;
}>({} as any);

export default function ConversationsListProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { unreadConversations } = useUnreadConversations();

  const [list, _setList] = useState<IConversation[]>([]);
  const { clientId } = useParams();
  const [params, setParams] = useState<QueryParams>({
    filters: {
      stared: undefined,
      archived: undefined,
      title_search: '',
      client_id: clientId,
      read: undefined,
    },
    limit: 10,
  });
  const [head, setHead] = useState<string | null>(null);
  const [tail, setTail] = useState<string | null>(null);

  const SetList = useMemo(
    () => (l: IConversation[]) => {
      const newlist = uniqBy(l, 'id')
        .filter((conv) => {
          if (params.filters.archived === true && !conv.archived_at) {
            return false;
          }
          if (params.filters.stared === true && !conv.stared_at) {
            return false;
          }
          if (params.filters.read === true && !conv.last_unread_message) {
            return false; // Show only read messages
          }
          return true;
        })
        .sort((a, b) => b.last_message.id - a.last_message.id);

      _setList(() => newlist);

      setHead((head) =>
        !!head && newlist.length > 0 ? newlist[0].cursor : null,
      );
      setTail((tail) =>
        !!tail && newlist.length > 0
          ? newlist[newlist.length - 1].cursor
          : null,
      );

      return newlist;
    },
    [_setList, params],
  );

  const [GetConversations] = useLazyGetPaginatedConversationsQuery();

  // not using the rtk query isLoading
  // because it caches the lazy response of past executions
  const [isLoading, setIsLoading] = useState(true);

  const fetchFirstPage = useMemo(
    () => () => {
      /*
     !! BUG (RTK Query) => refetching same request when no parameters was changed 
     returns a cached response of last request although it is correct in the redux store
     */
      GetConversations({
        ...params,
        start_from: undefined,
        direction: undefined,
        limit: 10,
      })
        .unwrap()
        .then(({ data, pagination }) => {
          setIsLoading(false);
          SetList(data);
          setHead((_) => pagination.start_cursor);
          setTail((_) =>
            pagination.has_next_page ? pagination.end_cursor : null,
          );
        });
    },
    [GetConversations, SetList, setHead, setTail, params],
  );

  const addConversation = useMemo(
    () => (conversation: IConversation) => {
      SetList([conversation].concat(list));
    },
    [SetList, list],
  );

  useEffect(fetchFirstPage, [JSON.stringify(params)]);
  const toggleIsRead = useMemo(
    () => () =>
      setParams({
        ...params,
        filters: {
          ...params.filters,
          read: params.filters.read ? undefined : true, // Toggle read filter
        },
      }),
    [setParams, params],
  );
  const toggleIsArchived = useMemo(
    () => () =>
      setParams({
        ...params,
        filters: {
          ...params.filters,
          archived: params.filters.archived ? undefined : true,
        },
      }),
    [setParams, params],
  );

  const toggleIsStared = useMemo(
    () => () =>
      setParams({
        ...params,
        filters: {
          ...params.filters,
          stared: params.filters.stared ? undefined : true,
        },
      }),
    [setParams, params],
  );

  const setSearch = useMemo(
    () => (title_search: string) =>
      setParams({
        ...params,
        filters: {
          ...params.filters,
          title_search,
        },
      }),
    [setParams, params],
  );

  const refreshTop = useMemo(
    () =>
      (limit: number = 10) => {
        if (list.length > 0 && !!head)
          GetConversations({
            filters: params.filters,
            start_from: head,
            direction: 'previous',
            limit,
          })
            .unwrap()
            .then(({ data, pagination }) => {
              SetList(data.concat(list));
              setHead((_) => pagination.start_cursor);
            });
      },
    [list, head, setHead, SetList, GetConversations, params],
  );

  const loadMore = useMemo(
    () => () => {
      if (list.length > 0 && !!tail)
        GetConversations({
          filters: params.filters,
          start_from: tail,
          direction: 'next',
          limit: 10,
        })
          .unwrap()
          .then(({ data, pagination }) => {
            SetList(list.concat(data));
            setTail((_) =>
              pagination.has_next_page ? pagination.end_cursor : null,
            );
          });
    },
    [list, tail, params, setTail, SetList, GetConversations],
  );

  const patchConversation = useMemo(
    () => (updated: IConversation) =>
      SetList(list.map((conv) => (conv.id === updated.id ? updated : conv))),
    [list, SetList],
  );

  const markAsRead = useMemo(
    () => (conversation: IConversation) =>
      patchConversation({ ...conversation, last_unread_message: undefined }),
    [patchConversation],
  );

  const value = {
    data: isLoading ? undefined : list,
    params,
    toggleIsArchived,
    toggleIsStared,
    toggleIsRead,
    loadMore,
    setSearch,
    addConversation,
    refreshTop,
    patchConversation,
    hasMore: !!tail,
    isLoading,
    markAsRead,
  };

  useEffect(() => {
    if (list.length > 0 && unreadConversations) {
      const lastestLoadedMessageDate = new Date(
        list[0].last_message.created_at,
      );
      const unreadConversationsLastMessagesDates = unreadConversations.map(
        (_) => _.last_message_created_at,
      );

      const newMessages = unreadConversationsLastMessagesDates.filter(
        (unread) => new Date(unread) > lastestLoadedMessageDate,
      );

      if (newMessages.length > 0) refreshTop(newMessages.length + 1); // the (+1) bug related to backend
    }
  }, [unreadConversations]);

  return (
    <ConversationsListContext.Provider value={value}>
      {children}
    </ConversationsListContext.Provider>
  );
}
